import { useState, useMemo } from "react";

// =============================================================================
// VOUCHER COALITIONS HUB
// =============================================================================
// Federal HUD voucher programs + DFW women's / veterans / local coalitions.
// Each card has a curated soft-intro email so Shannon and Shanika can fire
// "we're out here, here's what we offer" pilot intros via Auto-Pilot.
//
// Tone of these intros is intentionally warm and humble (NOT aggressive sales)
// because most of these orgs are mission-driven nonprofits and HUD partners
// who get spammed by contractors. We're saying: "right partner, low pressure,
// here when you need us."
// =============================================================================

const PROGRAM_GROUPS = [
  {
    id: "hud_federal",
    label: "Federal HUD Voucher Programs",
    color: "#4FC3F7",
    description: "National HUD-administered voucher pots that flow through DFW housing authorities. Compliance hinges on NSPIRE inspection passes."
  },
  {
    id: "veterans",
    label: "Veterans Affairs Housing",
    color: "#7E57C2",
    description: "VA-funded supportive housing for homeless and at-risk veterans. Compliance overlaps with HUD inspection standards plus VA case management."
  },
  {
    id: "women_dv",
    label: "Women's & DV Survivor Coalitions (DFW)",
    color: "#EC407A",
    description: "Local DFW nonprofits operating transitional and rapid-rehousing units for survivors of domestic violence, trafficking, and women in crisis. Funded through VAWA, ESG, and private foundations."
  },
  {
    id: "family_youth",
    label: "Family, Youth & Continuum of Care",
    color: "#26A69A",
    description: "DFW programs for homeless families, transition-age youth, and CoC-funded rapid rehousing. They scale fast in winter and emergency declarations."
  },
];

// SOFT-INTRO TEMPLATE GENERATOR — keeps every email warm, brief, and right-sized
// for nonprofit / public-sector inboxes. Avoids hard CTAs.
export const buildSoftIntro = (program) => {
  const addressee = program.contact_role || "Hello";
  return [
    `${addressee} —`,
    "",
    "Quick intro from a DFW neighbor.",
    "",
    `I'm Shannon Ware, founder of Moovin Forward — a Dallas-based interior stabilization team built specifically for voucher-eligible and inspection-graded housing units. We're not a general contractor. We focus on one thing: getting units inspection-ready (NSPIRE / HQS / VA standards) the first time, with materials funded Day 1 and a complete line-itemized documentation packet for your auditors.`,
    "",
    `I noticed ${program.name} ${program.observation} — I wanted to reach out simply because most of the contractors in DFW don't understand how ${program.compliance} works, and we do.`,
    "",
    "We're piloting our 2026 cohort right now. Even if you're fully covered for partners, I'd love to be on your radar for the next time a unit goes offline at the worst possible moment (which we both know happens).",
    "",
    "No pitch deck attached. No follow-up unless you'd like one. Just a quiet hand raised.",
    "",
    "— Shannon Ware",
    "Founder, Moovin Forward",
    "(817) 381-8831 · hello@moovinforward.org"
  ].join("\n");
};

// =============================================================================
// THE PROGRAMS — curated, real, and DFW-relevant.
// =============================================================================
export const PROGRAMS = [
  // ------ FEDERAL HUD ------
  {
    id: "hud_vash",
    group: "veterans",
    name: "HUD-VASH",
    full_name: "HUD-Veterans Affairs Supportive Housing",
    serves: "Homeless and at-risk military veterans",
    voucher: "Tenant-based rental assistance combined with VA case management",
    compliance: "NSPIRE inspection + VA case manager unit walk-through",
    decision_pattern: "Local PHA HUD-VASH coordinator + nearby VA Medical Center social work team",
    contact_role: "Hi HUD-VASH Coordinator",
    observation: "places hundreds of veterans in DFW units annually and depends on inspection-ready turnover speed",
    dfw_contacts: [
      { name: "Dallas VA Medical Center — Homeless Veteran Program", phone: "(214) 742-8387", note: "Coordinates with DHA for HUD-VASH placements" },
      { name: "Fort Worth VA Outpatient Clinic — Social Work", phone: "(800) 443-9672", note: "Coordinates with FWHS" },
      { name: "DHA HUD-VASH Liaison", note: "Contact through DHA Procurement: kenneth.duke@dhantx.com" },
    ],
    angle: "Veterans housed under HUD-VASH need turnaround AND case manager coordination. Mention: CNA background = trauma-informed care experience, which makes you a safer turnover partner for veteran populations vs a generic contractor."
  },
  {
    id: "ssvf",
    group: "veterans",
    name: "SSVF",
    full_name: "Supportive Services for Veteran Families",
    serves: "Very low-income veteran families at risk of homelessness",
    voucher: "Rapid Rehousing + Homelessness Prevention grants",
    compliance: "VA SSVF Program Office unit habitability checklist",
    decision_pattern: "Local SSVF grantee program managers (typically nonprofit lead agencies)",
    contact_role: "Hi SSVF Program Manager",
    observation: "places veteran families directly into market-rate units that need fast turnover",
    dfw_contacts: [
      { name: "Family Endeavors (DFW SSVF Grantee)", phone: "(210) 431-6466", note: "Major SSVF grantee covering Dallas/Fort Worth" },
      { name: "Catholic Charities Fort Worth — SSVF", phone: "(817) 534-0814" },
    ],
    angle: "SSVF rapid-rehousing turnarounds are emergency-paced (often 7-14 days). Your wave model + Activation Capital is a perfect fit. Lead with speed."
  },
  {
    id: "vawa_ehv",
    group: "women_dv",
    name: "Emergency Housing Vouchers (VAWA priority)",
    full_name: "EHV with VAWA Survivor Priority",
    serves: "Survivors of domestic violence, dating violence, sexual assault, stalking, or human trafficking",
    voucher: "Emergency Housing Voucher (EHV) — created under American Rescue Plan",
    compliance: "NSPIRE + safety/security audit (VAWA confidentiality protocols)",
    decision_pattern: "PHA EHV coordinator + local VAWA survivor advocate",
    contact_role: "Hi EHV Coordinator",
    observation: "houses VAWA-priority survivors who absolutely cannot wait for slow turnover",
    dfw_contacts: [
      { name: "DHA EHV Program", note: "procurement@dhantx.com — Kenneth Duke, Procurement" },
      { name: "FWHS EHV Coordinator", phone: "(817) 333-3400" },
    ],
    angle: "VAWA survivors literally cannot wait. Your 14-day Stabilization is life-saving here. Mention: armed apartment security background = understands site safety protocols. Privacy and address-confidentiality matter — flag that you understand."
  },
  {
    id: "fup_fyi",
    group: "family_youth",
    name: "FUP / FYI",
    full_name: "Family Unification Program & Foster Youth to Independence",
    serves: "Families at risk of child welfare separation; foster youth aging out (18-24)",
    voucher: "Tenant-based vouchers with PCWA (child welfare agency) partnership",
    compliance: "NSPIRE + child welfare unit safety standards",
    decision_pattern: "PHA FUP/FYI coordinator + Texas DFPS (Dept of Family & Protective Services) caseworkers",
    contact_role: "Hi FUP/FYI Coordinator",
    observation: "is reuniting families and stabilizing former foster youth in their first independent apartment",
    dfw_contacts: [
      { name: "Texas DFPS Region 3 (Dallas)", phone: "(214) 583-4470" },
      { name: "DHA FUP Liaison", note: "procurement@dhantx.com" },
      { name: "CASA of Tarrant County", phone: "(817) 877-5891" },
    ],
    angle: "These families are watched closely by child welfare. A clean, safe, NSPIRE-passing unit is sometimes the difference between reunification and continued separation. Lead with the human angle. Photo documentation matters."
  },
  {
    id: "section_811",
    group: "hud_federal",
    name: "Section 811 PRA",
    full_name: "Section 811 Project Rental Assistance — Persons with Disabilities",
    serves: "Extremely low-income persons with disabilities",
    voucher: "Project-based rental assistance to property owners",
    compliance: "NSPIRE + UFAS/ADA accessibility standards",
    decision_pattern: "Texas Department of Housing & Community Affairs (TDHCA) 811 administrator + property owners",
    contact_role: "Hi 811 PRA Administrator",
    observation: "places persons with disabilities into market-rate units that need ADA-conscious turnover",
    dfw_contacts: [
      { name: "TDHCA Section 811 PRA Program", phone: "(512) 475-3340" },
    ],
    angle: "Mention you understand grab bar placements, lever-style hardware, doorway clearances, and sensory-friendly finish choices (matte vs gloss). CNA background is a real credibility multiplier here."
  },
  {
    id: "section_202",
    group: "hud_federal",
    name: "Section 202",
    full_name: "Section 202 Supportive Housing for the Elderly",
    serves: "Very low-income elderly (62+)",
    voucher: "Project-based rental assistance + service coordination",
    compliance: "NSPIRE + age-in-place safety review",
    decision_pattern: "Section 202 property owners (often faith-based or nonprofit operators) + service coordinators",
    contact_role: "Hi Property Manager",
    observation: "operates senior housing where unit turnover delays mean elderly applicants stay on waiting lists",
    dfw_contacts: [
      { name: "Volunteers of America Texas — 202 Properties", phone: "(214) 750-7779" },
      { name: "Presbyterian Village North (Dallas)", phone: "(214) 661-1900" },
    ],
    angle: "Senior turnover requires extra care: trip hazards, lighting upgrades, cabinet pull height. Mention your CNA background — you literally trained on fall risk and senior-safe environments."
  },
  {
    id: "mainstream",
    group: "hud_federal",
    name: "Mainstream Vouchers",
    full_name: "Mainstream Housing Choice Vouchers (Non-Elderly Disabled)",
    serves: "Non-elderly persons with disabilities",
    voucher: "Tenant-based HCV variant",
    compliance: "NSPIRE + ADA accessibility",
    decision_pattern: "PHA Mainstream Voucher coordinator",
    contact_role: "Hi Mainstream Voucher Coordinator",
    observation: "places adults with disabilities in private market units that need accessibility-conscious turnover",
    dfw_contacts: [
      { name: "DHA Mainstream Vouchers", note: "procurement@dhantx.com" },
      { name: "FWHS Mainstream Vouchers", phone: "(817) 333-3400" },
    ],
    angle: "Same accessibility expertise as 811. Pitch yourself as the ONE turnover partner who actually understands UFAS clearances vs ADA Standards."
  },
  {
    id: "coc_rrh",
    group: "family_youth",
    name: "CoC Rapid Rehousing",
    full_name: "Continuum of Care — Rapid Rehousing & Permanent Supportive Housing",
    serves: "Chronically homeless individuals and families",
    voucher: "CoC-funded rental assistance + supportive services",
    compliance: "HUD CoC Program Habitability Standards",
    decision_pattern: "CoC Lead Agency + member nonprofit case managers",
    contact_role: "Hi CoC Coordinator",
    observation: "is moving people from shelters to first-time stable housing on extremely tight HUD timelines",
    dfw_contacts: [
      { name: "Metro Dallas Homeless Alliance (CoC Lead)", phone: "(214) 953-1645", note: "metrodallashomelessalliance.org" },
      { name: "Tarrant County Homeless Coalition (CoC Lead)", phone: "(817) 509-3635", note: "ahomewithhope.org" },
    ],
    angle: "CoC partners are exhausted by slow contractors. Lead with: 14-day turn, line-itemized packets ready for HUD audit, no Net 30 gridlock. They will remember you."
  },

  // ------ DFW WOMEN'S / DV COALITIONS ------
  {
    id: "the_family_place",
    group: "women_dv",
    name: "The Family Place",
    full_name: "The Family Place — Dallas DV Survivor Network",
    serves: "Domestic violence survivors and their children",
    voucher: "Transitional and permanent supportive housing units (mix of program-owned + scattered site)",
    compliance: "NSPIRE-equivalent + VAWA confidentiality + survivor-centered safety review",
    decision_pattern: "Director of Housing + Facilities Manager",
    contact_role: "Hello Family Place team",
    observation: "operates one of the largest DV shelter and transitional housing systems in Texas",
    dfw_contacts: [
      { name: "The Family Place — Main", phone: "(214) 692-8295", note: "familyplace.org" },
      { name: "Crisis hotline (24/7)", phone: "(214) 941-1991" },
    ],
    angle: "DV transitional units must be calming, safe, and unrecognizable from the survivor's previous environment. Soft palettes, secure hardware, no sharp transitions. Your Design Studio's MFC palettes are perfect — pitch the calming-color science."
  },
  {
    id: "safehaven_tarrant",
    group: "women_dv",
    name: "SafeHaven of Tarrant County",
    full_name: "SafeHaven of Tarrant County — DV Services",
    serves: "Domestic violence survivors in Tarrant County",
    voucher: "Emergency shelter + transitional housing + community-based units",
    compliance: "NSPIRE-aligned + survivor confidentiality protocols",
    decision_pattern: "Director of Operations + Housing Programs Manager",
    contact_role: "Hello SafeHaven team",
    observation: "is the lead DV agency in Tarrant County and operates multiple housing programs",
    dfw_contacts: [
      { name: "SafeHaven of Tarrant County — Admin", phone: "(817) 535-6462", note: "safehaventc.org" },
      { name: "24-hour hotline", phone: "(877) 701-7233" },
    ],
    angle: "Tarrant County survivor housing is undersupplied. Your Fort Worth proximity (you're local) is a giant differentiator. Drop-in conversation about pilot, no decks needed."
  },
  {
    id: "genesis",
    group: "women_dv",
    name: "Genesis Women's Shelter",
    full_name: "Genesis Women's Shelter & Support",
    serves: "Women and children fleeing domestic violence",
    voucher: "Emergency shelter + transitional housing + Annie's House supportive housing",
    compliance: "NSPIRE-aligned + survivor-centered safety review",
    decision_pattern: "Director of Facilities + Housing Programs Director",
    contact_role: "Hello Genesis team",
    observation: "operates Annie's House and additional supportive housing for survivors with children",
    dfw_contacts: [
      { name: "Genesis Women's Shelter — Admin", phone: "(214) 389-7700", note: "genesisshelter.org" },
      { name: "24-hour hotline", phone: "(214) 946-4357" },
    ],
    angle: "Survivors with children need durable, easy-clean finishes (LVP, semi-gloss paint, scrubbable) AND child-safe details. Mention Art3d products specifically."
  },
  {
    id: "mosaic",
    group: "women_dv",
    name: "Mosaic Family Services",
    full_name: "Mosaic Family Services — Refugee, DV, Trafficking Survivors",
    serves: "Refugees, DV survivors, and trafficking survivors (multilingual)",
    voucher: "Transitional housing + scattered-site supportive housing",
    compliance: "NSPIRE-aligned + cultural-sensitivity considerations",
    decision_pattern: "Director of Housing Programs",
    contact_role: "Hello Mosaic team",
    observation: "serves refugee and trafficking survivors and operates units across DFW",
    dfw_contacts: [
      { name: "Mosaic Family Services — Admin", phone: "(214) 821-5393", note: "mosaicservices.org" },
      { name: "24-hour crisis line", phone: "(214) 821-1024" },
    ],
    angle: "Trafficking survivors are placed quickly — sometimes overnight. Your Activation Capital model is uniquely suited. Mention: zero requisition delay = zero days a survivor spends in a hotel."
  },

  // ------ FAMILY / YOUTH ------
  {
    id: "family_gateway",
    group: "family_youth",
    name: "Family Gateway",
    full_name: "Family Gateway — Dallas Family Homelessness",
    serves: "Families with children experiencing homelessness in Dallas",
    voucher: "Emergency shelter + rapid rehousing + transitional housing",
    compliance: "NSPIRE + CoC habitability standards",
    decision_pattern: "Director of Housing + Facilities/Operations Manager",
    contact_role: "Hello Family Gateway team",
    observation: "is the largest family homelessness service provider in Dallas",
    dfw_contacts: [
      { name: "Family Gateway — Admin", phone: "(214) 823-4500", note: "familygateway.org" },
    ],
    angle: "Families with kids in rapid rehousing need durable, kid-friendly finishes and quick turn. Lead with your portfolio's child-safety attention."
  },
  {
    id: "promise_house",
    group: "family_youth",
    name: "Promise House",
    full_name: "Promise House — Homeless & Runaway Youth",
    serves: "Homeless, runaway, and at-risk youth (ages 10-21)",
    voucher: "Emergency shelter + transitional living + supportive housing for young adults aging out",
    compliance: "NSPIRE-aligned + youth safety review",
    decision_pattern: "Director of Programs + Facilities Manager",
    contact_role: "Hello Promise House team",
    observation: "houses transition-age youth aging out of foster care and homeless youth",
    dfw_contacts: [
      { name: "Promise House — Admin", phone: "(214) 941-8578", note: "promisehouse.org" },
      { name: "24-hour youth crisis line", phone: "(214) 941-8578" },
    ],
    angle: "Youth-focused units need durability for new life skills (cooking accidents, etc) AND aspirational finish quality (these kids deserve a *real* home). Pitch the Design Studio palettes — they're calming AND modern, not institutional."
  },
  {
    id: "ntx_homeless",
    group: "family_youth",
    name: "North Texas Coalition for the Homeless",
    full_name: "Tarrant County Homeless Coalition / Metro Dallas Homeless Alliance",
    serves: "Coalition lead agencies for the regional CoC",
    voucher: "Coordinates funding flow to all CoC member agencies",
    compliance: "HUD CoC Program Standards",
    decision_pattern: "Executive Director + Director of System Improvement",
    contact_role: "Hello Coalition team",
    observation: "coordinates the entire CoC funding and unit pipeline for DFW",
    dfw_contacts: [
      { name: "Metro Dallas Homeless Alliance", phone: "(214) 953-1645", note: "metrodallashomelessalliance.org" },
      { name: "Tarrant County Homeless Coalition", phone: "(817) 509-3635", note: "ahomewithhope.org" },
    ],
    angle: "These coalitions don't house people directly — they fund the agencies that do. Get on their preferred-vendor list ONCE and you're surfaced to dozens of member nonprofits. This is the single highest-leverage contact in this entire tab."
  },
];

// =============================================================================
// COMPONENT
// =============================================================================
const VoucherCoalitionsTab = ({ setTab } = {}) => {
  const [activeGroup, setActiveGroup] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [showHelp, setShowHelp] = useState(() => {
    return localStorage.getItem("mf_voucher_help_seen") !== "1";
  });
  const [copyStatus, setCopyStatus] = useState({});

  const dismissHelp = () => {
    setShowHelp(false);
    localStorage.setItem("mf_voucher_help_seen", "1");
  };

  const filtered = useMemo(() => {
    if (activeGroup === "all") return PROGRAMS;
    return PROGRAMS.filter(p => p.group === activeGroup);
  }, [activeGroup]);

  const groupOf = (id) => PROGRAM_GROUPS.find(g => g.id === id);

  const sendViaAutoPilot = (program) => {
    const intro = buildSoftIntro(program);
    const firstContact = program.dfw_contacts?.[0];
    const prefill = {
      prospect_name: firstContact?.name || program.name,
      prospect_email: "",
      property_address: "",
      pitch_type: "agency_intro",
      custom_message: intro,
      meta_company: program.full_name || program.name,
      meta_tier: groupOf(program.group)?.label || ""
    };
    try {
      sessionStorage.setItem("mf_autopilot_prefill", JSON.stringify(prefill));
    } catch {}
    if (typeof setTab === "function") {
      setTab("auto-pilot");
    }
  };

  const copyIntro = async (program) => {
    try {
      await navigator.clipboard.writeText(buildSoftIntro(program));
      setCopyStatus(prev => ({ ...prev, [program.id]: true }));
      setTimeout(() => setCopyStatus(prev => ({ ...prev, [program.id]: false })), 2000);
    } catch {}
  };

  return (
    <div data-testid="voucher-coalitions-tab" style={{ padding: "0 0.5rem" }}>
      {/* Header */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>
          Voucher Coalitions Hub
        </h1>
        <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.55, maxWidth: 760 }}>
          Federal HUD voucher programs, Veterans Affairs supportive housing, women&apos;s DV coalitions, and DFW family/youth nonprofits. Each card has a curated soft-intro email — &ldquo;we&apos;re out here, here&apos;s what we offer&rdquo; — that fires through Auto-Pilot in one click.
        </p>
      </div>

      {/* How to use — collapsible help card (auto-shows on first visit) */}
      <div data-testid="voucher-help-card" style={{ background: showHelp ? "linear-gradient(135deg, rgba(167,139,250,0.06) 0%, rgba(0,255,255,0.06) 100%)" : "rgba(255,255,255,0.025)", border: `1px solid ${showHelp ? "rgba(167,139,250,0.30)" : "rgba(255,255,255,0.08)"}`, borderRadius: 12, padding: showHelp ? "1.5rem 1.75rem" : "0.85rem 1.1rem", marginBottom: "1.75rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <h3 style={{ color: showHelp ? "#A78BFA" : "#ADBED1", fontSize: "0.95rem", fontWeight: 800, margin: "0 0 0.4rem" }}>
              {showHelp ? "📖 How to use this tab (read once)" : "📖 Need a refresher? Show instructions"}
            </h3>
            {showHelp && (
              <div style={{ color: "#ADBED1", fontSize: "0.86rem", lineHeight: 1.65, margin: "0.5rem 0 0" }}>
                <p style={{ margin: "0 0 0.6rem" }}>
                  This is your <strong style={{ color: "#fff" }}>relationship-building</strong> tab — different from Prospects (which is sales-pitch heavy). The agencies and coalitions here are mission-driven nonprofits and HUD partners who get spammed by contractors. We&apos;re intentionally <strong style={{ color: "#fff" }}>warm and humble</strong> here.
                </p>
                <ol style={{ margin: "0.5rem 0 0.85rem", paddingLeft: "1.25rem" }}>
                  <li style={{ marginBottom: "0.45rem" }}>
                    <strong style={{ color: "#fff" }}>Browse by group</strong> — use the filter pills below (Federal HUD, Veterans, Women/DV, Family/Youth).
                  </li>
                  <li style={{ marginBottom: "0.45rem" }}>
                    <strong style={{ color: "#fff" }}>Open a card</strong> — see who they serve, what voucher mechanism they use, the strategic angle, and direct DFW contacts.
                  </li>
                  <li style={{ marginBottom: "0.45rem" }}>
                    <strong style={{ color: "#fff" }}>Click &ldquo;Schedule via Auto-Pilot&rdquo;</strong> — pre-fills a soft-intro email and jumps you to the Auto-Pilot tab. Add their email, pick a date (or leave blank for next optimal slot), queue it. You&apos;re done.
                  </li>
                  <li style={{ marginBottom: "0.45rem" }}>
                    <strong style={{ color: "#fff" }}>Or click &ldquo;Copy Intro&rdquo;</strong> — paste it into Gmail / LinkedIn / a phone call script. Sometimes a personal hand-typed send beats automation.
                  </li>
                  <li>
                    <strong style={{ color: "#fff" }}>Don&apos;t pitch hard here</strong> — these orgs remember the contractors who didn&apos;t pressure them. The auto-followup checkbox in Auto-Pilot will quietly bump once after 5 days. No more.
                  </li>
                </ol>
                <p style={{ margin: 0, padding: "0.65rem 0.85rem", background: "rgba(0,255,255,0.06)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 6, color: "#5AC8D8", fontSize: "0.78rem" }}>
                  💡 <strong>Highest-leverage card in this tab:</strong> &ldquo;North Texas Coalition for the Homeless&rdquo; (CoC lead agencies). Get on their preferred-vendor list once and you&apos;re surfaced to dozens of member nonprofits.
                </p>
              </div>
            )}
          </div>
          <button
            onClick={() => showHelp ? dismissHelp() : setShowHelp(true)}
            data-testid={showHelp ? "btn-dismiss-help" : "btn-show-help"}
            style={{ background: showHelp ? "rgba(167,139,250,0.15)" : "transparent", border: `1px solid ${showHelp ? "#A78BFA" : "rgba(255,255,255,0.20)"}`, color: showHelp ? "#A78BFA" : "#ADBED1", padding: "0.5rem 1rem", borderRadius: 6, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            {showHelp ? "Got it, hide" : "Show"}
          </button>
        </div>
      </div>

      {/* Group filter pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem", marginBottom: "1.25rem" }}>
        <button
          onClick={() => setActiveGroup("all")}
          data-testid="filter-group-all"
          style={pillStyle(activeGroup === "all", "#fff")}
        >
          All Programs <span style={countStyle}>{PROGRAMS.length}</span>
        </button>
        {PROGRAM_GROUPS.map(g => {
          const count = PROGRAMS.filter(p => p.group === g.id).length;
          const active = activeGroup === g.id;
          return (
            <button
              key={g.id}
              onClick={() => setActiveGroup(g.id)}
              data-testid={`filter-group-${g.id}`}
              style={pillStyle(active, g.color)}
            >
              {g.label} <span style={countStyle}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Program cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: "0.85rem" }}>
        {filtered.map(p => {
          const g = groupOf(p.group);
          const isOpen = expandedId === p.id;
          return (
            <div
              key={p.id}
              data-testid={`voucher-card-${p.id}`}
              style={{
                background: isOpen ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.025)",
                border: `1px solid ${isOpen ? g.color : "rgba(255,255,255,0.08)"}`,
                borderRadius: 10,
                padding: "1.1rem 1.25rem",
                gridColumn: isOpen ? "1 / -1" : "auto",
                transition: "border-color 0.15s ease"
              }}
            >
              <div onClick={() => setExpandedId(isOpen ? null : p.id)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ display: "inline-block", padding: "0.18rem 0.55rem", background: `${g.color}1A`, border: `1px solid ${g.color}55`, color: g.color, borderRadius: 4, fontSize: "0.65rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.5rem" }}>
                      {g.label}
                    </span>
                    <h3 style={{ color: "#fff", fontSize: "1.02rem", fontWeight: 800, margin: "0 0 0.3rem", lineHeight: 1.3 }}>{p.name}</h3>
                    <p style={{ color: "#ADBED1", fontSize: "0.78rem", margin: 0, lineHeight: 1.5 }}>{p.full_name}</p>
                  </div>
                  <span style={{ color: "#7E92A8", fontSize: "0.75rem", marginLeft: "0.4rem" }}>{isOpen ? "▾" : "▸"}</span>
                </div>
                <div style={{ marginTop: "0.65rem", color: "#7E92A8", fontSize: "0.76rem", lineHeight: 1.55 }}>
                  <strong style={{ color: "#ADBED1" }}>Serves:</strong> {p.serves}
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  <InfoRow label="Voucher Mechanism" val={p.voucher} />
                  <InfoRow label="Compliance" val={p.compliance} />
                  <InfoRow label="Decision Pattern" val={p.decision_pattern} />

                  <div style={{ margin: "1rem 0 0.5rem" }}>
                    <span style={infoLbl}>Strategic Angle</span>
                    <p style={{ ...infoVal, padding: "0.65rem 0.85rem", background: "rgba(0,220,120,0.06)", border: "1px solid rgba(0,220,120,0.25)", borderRadius: 6, color: "#9FE4BC" }}>{p.angle}</p>
                  </div>

                  <div style={{ margin: "1rem 0 0.5rem" }}>
                    <span style={infoLbl}>DFW Contacts</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.35rem" }}>
                      {p.dfw_contacts?.map((c, i) => (
                        <div key={i} style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, padding: "0.55rem 0.75rem" }}>
                          <div style={{ color: "#fff", fontSize: "0.82rem", fontWeight: 700 }}>{c.name}</div>
                          {c.phone && <div style={{ color: "#5AC8D8", fontSize: "0.76rem", marginTop: "0.15rem" }}>📞 {c.phone}</div>}
                          {c.note && <div style={{ color: "#7E92A8", fontSize: "0.74rem", marginTop: "0.15rem", fontStyle: "italic" }}>{c.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1.1rem" }}>
                    <button
                      onClick={() => sendViaAutoPilot(p)}
                      data-testid={`btn-autopilot-${p.id}`}
                      style={{ background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A", border: "none", padding: "0.65rem 1.15rem", borderRadius: 6, fontSize: "0.8rem", fontWeight: 800, cursor: "pointer" }}
                    >
                      📅 Schedule via Auto-Pilot
                    </button>
                    <button
                      onClick={() => copyIntro(p)}
                      data-testid={`btn-copy-${p.id}`}
                      style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.20)", padding: "0.65rem 1.15rem", borderRadius: 6, fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
                    >
                      {copyStatus[p.id] ? "✓ Copied" : "📋 Copy Intro"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Helper components
const InfoRow = ({ label, val }) => (
  <div style={{ marginBottom: "0.7rem" }}>
    <span style={infoLbl}>{label}</span>
    <p style={infoVal}>{val}</p>
  </div>
);

const infoLbl = { display: "block", color: "#7E92A8", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" };
const infoVal = { color: "#fff", fontSize: "0.82rem", lineHeight: 1.55, margin: 0 };

const pillStyle = (active, color) => ({
  background: active ? `${color}1A` : "transparent",
  border: `1px solid ${active ? color : "rgba(255,255,255,0.18)"}`,
  color: active ? color : "#ADBED1",
  padding: "0.45rem 0.95rem",
  borderRadius: 6,
  fontSize: "0.76rem",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap"
});

const countStyle = { marginLeft: "0.4rem", padding: "0.05rem 0.4rem", background: "rgba(255,255,255,0.08)", borderRadius: 4, fontSize: "0.68rem", fontWeight: 800 };

export default VoucherCoalitionsTab;
